/**
 * S-60 漁場情報一覧（漁獲調査）＋ 漁獲CSV取込ダイアログ（F-CAT-01 / F-CAT-02）。担当: G4
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FileUp, Fish, Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  Modal,
  PageHeader,
  SearchPanel,
  Select,
  TextField,
  useToast,
  type Column,
} from '@/components';
import { hasRole, READ_ONLY_ROLES } from '@/auth/types';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect, useCodeValues } from '@/features/masters';
import { errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { useFishingGrounds, useImportCatchCsv, type CsvImportResult, type FishingGroundFilter, type FishingGroundListItem } from './api';
import './catch.css';

function useFilterFromUrl(): [FishingGroundFilter, (f: FishingGroundFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<FishingGroundFilter>(
    () => ({
      businessOperatorId: params.get('businessOperatorId') ? Number(params.get('businessOperatorId')) : null,
      registrationNo: params.get('registrationNo') ?? '',
      vesselName: params.get('vesselName') ?? '',
      voyageFrom: params.get('voyageFrom') ?? '',
      voyageTo: params.get('voyageTo') ?? '',
      fishingMethod: params.get('fishingMethod') ?? '',
    }),
    [params],
  );
  const setFilter = (f: FishingGroundFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v != null && v !== '' && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

export function FishingGroundListPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { year, yearPath } = useFiscalYear();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useFishingGrounds(year, filter);
  const codes = useCodeValues();
  const { register, handleSubmit, reset, control } = useForm<FishingGroundFilter>({ values: filter });
  const [csvOpen, setCsvOpen] = useState(false);
  const canEdit = !hasRole(user, READ_ONLY_ROLES);

  const columns: Column<FishingGroundListItem>[] = [
    { key: 'businessOperatorName', header: '事業実施者', sortable: true, width: 170 },
    { key: 'registrationNo', header: '漁船登録番号', sortable: true, width: 130, render: (r) => <span className="bs-num">{r.registrationNo}</span> },
    { key: 'vesselName', header: '船名', sortable: true },
    { key: 'tonnage', header: 'トン数', numeric: true, sortable: true, width: 80, accessor: (r) => r.tonnage ?? 0, render: (r) => (r.tonnage == null ? '' : formatNumber(r.tonnage, 2)) },
    { key: 'fishingMethod', header: '漁法', sortable: true, width: 120 },
    {
      key: 'voyageFrom',
      header: '調査航海期間',
      sortable: true,
      width: 210,
      render: (r) => (
        <span className="bs-num">
          {r.voyageFrom} 〜 {r.voyageTo}
        </span>
      ),
    },
    { key: 'landingDate', header: '水揚げ日', sortable: true, width: 110, render: (r) => <span className="bs-num">{r.landingDate ?? ''}</span> },
    { key: 'landingTotalInclTax', header: '水揚合計額（税込）', numeric: true, sortable: true, width: 140 },
    { key: 'entryCount', header: '明細', numeric: true, width: 70 },
    { key: 'reporterName', header: '報告者', width: 110 },
  ];

  const summary = [filter.registrationNo && `登録番号: ${filter.registrationNo}`, filter.vesselName && `船名: ${filter.vesselName}`, (filter.voyageFrom || filter.voyageTo) && `期間: ${filter.voyageFrom ?? ''}〜${filter.voyageTo ?? ''}`, filter.fishingMethod]
    .filter(Boolean)
    .join(' ／ ');

  return (
    <>
      <PageHeader
        eyebrow="日々の報告"
        title="漁場情報一覧（漁獲調査）"
        description="出港ごとの漁場情報（漁獲位置・魚種・水揚げ）を登録します。漁協の販売システムの水揚データは「漁獲CSV取込」から取り込めます。"
        actions={
          canEdit && (
            <>
              <Button icon={<FileUp size={16} />} onClick={() => setCsvOpen(true)}>
                漁獲CSV取込
              </Button>
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => navigate(yearPath('fishing-grounds/new'))}>
                新規登録
              </Button>
            </>
          )
        }
      />
      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter(v))}
        onReset={() => {
          reset({ businessOperatorId: null, registrationNo: '', vesselName: '', voyageFrom: '', voyageTo: '', fishingMethod: '' });
          setFilter({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={4}
      >
        <FormField label="事業実施者">
          <Controller
            control={control}
            name="businessOperatorId"
            render={({ field }) => <BusinessOperatorSelect fiscalYear={year} value={field.value ?? null} onChange={field.onChange} width="full" />}
          />
        </FormField>
        <FormField label="漁船登録番号">
          <TextField {...register('registrationNo')} placeholder="部分一致" />
        </FormField>
        <FormField label="船名" hint="船名・カナの部分一致">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="漁法">
          <Controller
            control={control}
            name="fishingMethod"
            render={({ field }) => (
              <Select
                placeholder="すべて"
                options={(codes.data?.catchFishingMethods ?? []).map((m) => ({ value: m.value, label: m.label }))}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </FormField>
        <FormField label="調査航海期間" span={2}>
          <div className="bs-catch-period">
            <DateField aria-label="調査航海期間（開始）" {...register('voyageFrom')} />
            <span>〜</span>
            <DateField aria-label="調査航海期間（終了）" {...register('voyageTo')} />
          </div>
        </FormField>
      </SearchPanel>
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="漁場情報一覧"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.id}
          loading={isPending}
          defaultSort={{ key: 'voyageFrom', direction: 'desc' }}
          pagination={{ pageSize: 50 }}
          onRowClick={(r) => navigate(yearPath(`fishing-grounds/${r.id}`))}
          empty={<EmptyState compact icon={<Fish />} title="該当する漁場情報はありません" description="検索条件を変更するか、新規登録してください。" />}
        />
      )}
      {csvOpen && <CsvImportDialog onClose={() => setCsvOpen(false)} />}
    </>
  );
}

/** 漁獲CSV取込ダイアログ（L-36） */
export function CsvImportDialog({ onClose }: { onClose: () => void }) {
  const { year } = useFiscalYear();
  const toast = useToast();
  const importCsv = useImportCatchCsv();
  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (operatorId == null) return setErr('事業実施者を選択してください');
    if (!file) return setErr('CSVファイルを選択してください');
    try {
      const r = await importCsv.mutateAsync({ file, businessOperatorId: operatorId });
      setResult(r);
      toast.success('漁獲CSVを取り込みました', `新規 ${r.imported} 件・上書き ${r.updated} 件・スキップ ${r.skipped} 件`);
    } catch (e) {
      setErr(errorMessage(e));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="漁獲CSV取込"
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>閉じる</Button>
          <Button variant="primary" icon={<FileUp size={16} />} loading={importCsv.isPending} onClick={() => void submit()}>
            取り込む
          </Button>
        </>
      }
    >
      <div className="bs-csv-import">
        <p className="bs-csv-import__note">
          漁協の販売システムから出力した水揚 CSV（Shift_JIS / UTF-8）を取り込みます。事業年度は水揚げ日から自動判定し（1〜3 月は前年度）、
          同じ船主・水揚げ日・魚種・出港日・入港日のデータは上書きします。
        </p>
        <FormField label="事業実施者" required>
          <BusinessOperatorSelect fiscalYear={year} value={operatorId} onChange={setOperatorId} placeholder="選択してください" width="lg" />
        </FormField>
        <FormField label="CSVファイル" required>
          <input
            type="file"
            accept=".csv,text/csv"
            aria-label="CSVファイル"
            className="bs-csv-import__file"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setResult(null);
            }}
          />
        </FormField>
        {err && (
          <p role="alert" className="bs-csv-import__error">
            {err}
          </p>
        )}
        {result && (
          <div className="bs-csv-import__result" aria-live="polite">
            <dl>
              <div>
                <dt>新規</dt>
                <dd className="bs-num">{formatNumber(result.imported)}</dd>
              </div>
              <div>
                <dt>上書き</dt>
                <dd className="bs-num">{formatNumber(result.updated)}</dd>
              </div>
              <div>
                <dt>スキップ</dt>
                <dd className="bs-num">{formatNumber(result.skipped)}</dd>
              </div>
              <div>
                <dt>文字コード</dt>
                <dd>{result.encoding === 'shift_jis' ? 'Shift_JIS' : 'UTF-8'}</dd>
              </div>
            </dl>
            {result.errors.length > 0 && (
              <ul className="bs-csv-import__errors">
                {result.errors.slice(0, 50).map((e) => (
                  <li key={`${e.line}-${e.message}`}>
                    <span className="bs-num">{e.line}</span> 行目：{e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
